/* eslint no-nested-ternary: 0 */
import { createGlobalStyle, ThemeProvider } from "styled-components";
import Head from "next/head";

import * as Layouts from "@layouts";

import "react-datepicker/dist/react-datepicker.css";

import { initRequestHandler } from "neofin-sdk";

import TagManager from "react-gtm-module";
import { useEffect, useState } from "react";
import LoadingSpinner from "@components/misc/LoadingSpinner";
import NoMobile from "@components/NoMobile";
import { useRouter } from "next/router";
import PageTransitionBar from "@components/PageTransitionBar";
import * as Sentry from "@sentry/nextjs";
import checkRouterUrls from "@utils/checkRouterUrls";
import { PopupProvider } from "@contexts/PopupContext";

const MIN_SCREEN_WIDTH = 600;

const GlobalStyle = createGlobalStyle`
  * {
    letter-spacing: 0.01em;
    box-sizing: border-box !important;
    font-family: 'Open Sans', sans-serif;
  }
  html {
    width: 100%;
  }
  body {
    margin: 0;
    padding: 0;
    color: #1a1a1a;
  }
  body a{
    text-decoration: none;
  }
`;

const theme = {};

const App = ({ Component, pageProps }) => {
  const router = useRouter();
  const [initializing, setInitializing] = useState(true);
  const Layout = Component.layout ? Layouts[Component.layout] : Layouts.Default;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    (async () => {
      await TagManager.initialize({ gtmId: process.env.TGM_ID });
    })();
    initRequestHandler({
      baseUrl: process.env.API_URL,
      eventHandler: (eventData) => {
        if (eventData.action === "failed") {
          const neofinUser = JSON.parse(
            window.localStorage.getItem("neofin-user") || ""
          );
          Sentry.captureException(
            new Error(
              `Request ${eventData.action} ${eventData.url}${
                eventData.statusCode ? ` - status ${eventData.statusCode}` : ""
              }${neofinUser?.mail ? ` - user ${neofinUser.mail}` : ""}`
            ),
            {
              extra: {
                ...eventData,
                user: neofinUser,
              },
              user: {
                email: neofinUser?.mail,
                username: neofinUser?.name,
                id: neofinUser?.id,
              },
            }
          );
        }

        TagManager.dataLayer({
          dataLayer: {
            event: "request_tracking",
            ...eventData,
          },
        });
      },
    });
    if (typeof window !== "undefined" && window.innerWidth < MIN_SCREEN_WIDTH)
      setIsMobile(true);
    setInitializing(false);
  }, []);

  const { hasMobile = false } = Component;

  const fixUrl = checkRouterUrls(router.asPath, router.pathname);

  return (
    <>
      <Head>
        <link rel="icon" href="/next_public/favicon.ico" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      <GlobalStyle />
      <PageTransitionBar />
      {isMobile && !hasMobile ? (
        fixUrl && fixUrl !== "/" ? (
          <LoadingSpinner size="lg" />
        ) : (
          <NoMobile />
        )
      ) : initializing ? (
        <LoadingSpinner size="lg" />
      ) : (
        <PopupProvider key={router.pathname}>
          <ThemeProvider theme={theme}>
            <Layout>
              <Component {...pageProps} />
            </Layout>
          </ThemeProvider>
        </PopupProvider>
      )}
    </>
  );
};

export default App;
